import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isAfter, isBefore, isEqual } from 'date-fns';

import Button from '../../../components/form/button/Button';
import TD from '../../../components/table/TD/TD';
import TR from '../../../components/table/TR/TR';
import deliveryZoneColorMap from '../../../core/constants/deliveryZoneColorMap';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import { unblockDate } from '../../../core/state/bookings/asyncActions';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { NotificationPosition } from '../../../core/state/global-notifications/types';
import BlockedSlot from '../../../core/types/model/BlockedSlot';
import Badge from '../../components/badge/Badge';
import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import { generateEntityFetchListAsyncAction } from '../../../core/state/entity/generateEntityAsyncActions';
import EntityName from '../../../core/enums/EntityName';
import { isReadAllRole } from '../../../core/state/user/selectors';
import { RootState } from '../../../core/state/state';
import { getTimeDate } from '../../../core/utils/date';

const getIsWholeDay = (firstDateStart: Date, firstDateEnd: Date, secondDateStart: Date, secondDateEnd: Date) => {
  return (
    (isEqual(firstDateStart, secondDateStart) || isBefore(firstDateStart, secondDateStart)) &&
    (isEqual(firstDateEnd, secondDateEnd) || isAfter(firstDateEnd, secondDateEnd))
  );
};

interface Props {
  slot: BlockedSlot;
}

const BlockBookingSlotsItem: React.FC<Props> = ({ slot }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUnblockBookingModalOpen, setIsUnblockBookingModalOpen] = React.useState<boolean>(false);
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const fetchBlockedSlots = React.useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);
  const isUserReader = useSelector(isReadAllRole);
  const { operatingHoursTo, operatingHoursFrom } = useSelector((state: RootState) => state.zones);
  const [startTime, endTime] = slot.timeSlot.split('-');
  const isWholeDay = getIsWholeDay(getTimeDate(startTime), getTimeDate(endTime), getTimeDate(operatingHoursFrom), getTimeDate(operatingHoursTo));

  const closeModalDialog = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        postition: NotificationPosition.Top,
        title: t('projectSettings.blockBookingSlots.unblockAllDates.cancel.title'),
        message: t('projectSettings.blockBookingSlots.unblockAllDates.cancel.text')
      })
    );
    setIsUnblockBookingModalOpen(false);
  };

  return (
    <>
      <TR key={slot.id}>
        <TD>{slot.blockedDayStr}</TD>
        <TD>{slot.blockedAllDay || isWholeDay ? t('projectSettings.blockBookingSlots.text.allDay') : slot.timeSlot}</TD>
        <TD>
          <div className="block-booking-slots__table-zones">
            {slot.zones.length < 10
              ? slot.zones.map((item) => <Badge key={item.slotId} color={deliveryZoneColorMap[item.order]} icon={item.order} label={item.name} />)
              : t('projectSettings.blockBookingSlots.text.allZones')}
          </div>
        </TD>
        <TD>{slot.comment ?? '--'}</TD>
        <TD>{slot.createdBy ?? '--'}</TD>
        {!isUserReader && (
          <TD>
            <Button className="block-booking-slots__actions-btn" onClick={() => setIsUnblockBookingModalOpen(true)} buttonTypes={['red-outline']}>
              {t('projectSettings.blockBookingSlots.button.unblock')}
            </Button>
          </TD>
        )}
      </TR>
      <ConfirmModal
        height={196}
        titleText={t('projectSettings.blockBookingSlots.unblockDate.title')}
        contentText={t('projectSettings.blockBookingSlots.unblockDate.text')}
        isOpen={isUnblockBookingModalOpen}
        onCancel={() => closeModalDialog()}
        onConfirm={async () => {
          const slotIds = slot.zones.map((s) => s.slotId).toString();
          await dispatch(unblockDate(slotIds));
          dispatch(fetchBlockedSlots({ projectId }));
          setIsUnblockBookingModalOpen(false);
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Success,
              postition: NotificationPosition.Top,
              message: t('projectSettings.blockBookingSlots.unblockDate.notification')
            })
          );
        }}
      />
    </>
  );
};

export default BlockBookingSlotsItem;
